import { useMutation } from '@apollo/client';
import { useCartState } from '../contexts/cart';
import mutation_consumables from '../aem-core-components/queries/mutation_consumables.graphql';
import { getConsumablesAction } from '../aem-core-components/actions/consumables';
import { generateSKU } from '../components/global/utils/commonUtils';
import { logError } from '../components/global/utils/logger';
import { UPDATE_CONSUMABLES } from '../aem-core-components/actions/constants';

const useConsumablesWithCache = () => {
    const [consumablesGQL] = useMutation(mutation_consumables);
    const [{ consumables }, dispatch] = useCartState();
    const { cachedConsumablesData } = consumables || {};

    const getCacheKey = (itemSku, pcId, account, jobNumber) => {
        return `${itemSku || ''}-${pcId || ''}-${account || ''}-${jobNumber || ''}`;
    };

    const splitCachedAndMissingData = (items, pcId, account, jobNumber) => {
        try {
            const cachedData = [];
            const missingItems = [];

            for (const itemSku of items) {
                const key = getCacheKey(itemSku, pcId, account, jobNumber);
                if (cachedConsumablesData?.[key]) {
                    cachedData?.push(cachedConsumablesData?.[key]);
                } else {
                    missingItems?.push(itemSku);
                }
            }

            return { cachedData, missingItems };
        } catch (error) {
            logError(error, false, 'splitCachedAndMissingData');
            return {};
        }
    };

    const storeInCache = (fetchedData, pcId, account, jobNumber) => {
        try {
            const updatedCache = { ...(cachedConsumablesData || {}) };
            for (const data of fetchedData) {
                if (data?.cat && data?.class) {
                    const sku = generateSKU(data?.cat, data?.class);
                    const key = getCacheKey(sku, pcId, account, jobNumber);
                    updatedCache[key] = data;
                }
            }
            dispatch({
                type: UPDATE_CONSUMABLES,
                key: 'cachedConsumablesData',
                value: updatedCache
            });
        } catch (error) {
            logError(error, false, 'storeInCache');
        }
    };

    const fetchConsumablesWithCache = async ({ items, pcId, account, jobNumber }) => {
        try {
            const { cachedData, missingItems } = splitCachedAndMissingData(items, pcId, account, jobNumber);
            let fetchedData = [];

            if (missingItems?.length > 0) {
                fetchedData = await getConsumablesAction({
                    consumablesGQL,
                    payloadForConsumables: {
                        items: missingItems,
                        pcId,
                        account,
                        jobNumber
                    }
                });
                storeInCache(fetchedData, pcId, account, jobNumber);
            }

            return [...(cachedData || []), ...(fetchedData || [])];
        } catch (error) {
            logError(error, false, 'fetchConsumablesWithCache', [items, pcId, account, jobNumber]);
            return [];
        }
    };

    return { fetchConsumablesWithCache };
};

export default useConsumablesWithCache;
